import { plateFont, numFont } from '../../utils/fonts';
import * as React from 'react';
import { Typography, IconButton, Tooltip, Box } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HOUR_HEIGHT } from './calendarConstants';
import { getHour, getMinutes, getDurationHours, parseISO } from './hooks/dateUtils';
import useCalendarColors from './hooks/useCalendarColors';
import useCalendarState from './hooks/useCalendarState';
import useHighlightScroll from './hooks/useHighlightScroll';
import DraggableEvent from './DraggableEvent';
import { MaintenanceTooltipContent } from './EventTooltipContent';

const PARTS_ORDER_CONFIG = {
  not_ordered: { color: '#ef4444', bg: '#fee2e2' },
  ordered: { color: '#d97706', bg: '#fef3c7' },
  arrived: { color: '#059669', bg: '#d1fae5' },
  not_needed: { color: '#64748b', bg: '#f1f5f9' },
};

function MaintenanceEvent({ event, startHour: workStartHour = 8, endHour: workEndHour = 16, overlapIndex = 0, overlapTotal = 1, dayIndex, resourceId }) {
  const { openEditEventDialog, highlightedMaintenanceId, highlightedScheduleId, employees, events, serviceBays, currentView } = useCalendarState();
  const { resolveEvent } = useCalendarColors();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // A work order can have several slots; a calendar search highlights just the one it
  // navigated to, while the ?highlight= URL param still highlights them all.
  const isHighlighted = highlightedScheduleId
    ? highlightedScheduleId === event.id
    : highlightedMaintenanceId === event.maintenance_id;

  useHighlightScroll(event.id, isHighlighted);

  // Guard against invalid event data (unless multi-day with pre-calculated hours)
  if (!event._clampedStartHour && (!event.start_time || !event.end_time)) {
    return null;
  }

  const handleClick = (e) => {
    e.stopPropagation();
    openEditEventDialog(event);
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    navigate(`/maintenances/${event.maintenance_id}`);
  };

  // Calculate raw and clamped hours for positioning and display
  const startTime = parseISO(event.start_time);
  const endTime = parseISO(event.end_time);
  const rawStartHour = getHour(startTime) + getMinutes(startTime) / 60;
  const rawEndHour = getHour(endTime) + getMinutes(endTime) / 60;

  let clampedStartHour, clampedEndHour;
  if (event._clampedStartHour !== undefined && event._clampedEndHour !== undefined) {
    // Multi-day event: use pre-calculated clamped hours for this day
    clampedStartHour = event._clampedStartHour;
    clampedEndHour = event._clampedEndHour;
  } else {
    // Single-day event: clamp to work schedule bounds to prevent overflow into other rows
    clampedStartHour = Math.max(workStartHour, rawStartHour);
    clampedEndHour = Math.min(workEndHour, rawEndHour);
  }

  const durationHours = clampedEndHour - clampedStartHour;
  const top = (clampedStartHour - workStartHour) * HOUR_HEIGHT + 2;
  const height = durationHours * HOUR_HEIGHT - 4;

  // Colours come from the resolver: organization state overrides, then the colour tag, and
  // the search highlight on top of both.
  const colors = resolveEvent(event, isHighlighted);
  const config = colors.stateConfig;

  // Format time from Date object (HH:mm with colon separator)
  const formatTimeFromDate = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Format short date (e.g., "7.1.")
  const formatShortDate = (date) => {
    return `${date.getDate()}.${date.getMonth() + 1}.`;
  };

  // Check if event spans multiple days
  const isMultiDay = event._isMultiDay || (startTime.toDateString() !== endTime.toDateString());

  // Build the time display string
  const getTimeDisplayString = () => {
    if (isMultiDay) {
      // Multi-day: show "7.1. 08:00 - 8.1. 10:00"
      return `${formatShortDate(startTime)} ${formatTimeFromDate(startTime)} - ${formatShortDate(endTime)} ${formatTimeFromDate(endTime)}`;
    } else {
      // Single-day: show "08:00 - 10:00"
      return `${formatTimeFromDate(startTime)} - ${formatTimeFromDate(endTime)}`;
    }
  };

  // Resolve employee names for tooltip
  const employeeNames = (event.employee_ids || [])
    .map((id) => employees.find((e) => e.id === id)?.name)
    .filter(Boolean)
    .join(', ');

  // Resolve the service bay name for the tooltip and the card (same lookup as AllViewEvent)
  const serviceBayName = event.service_bay_id
    ? serviceBays.find((b) => b.id === event.service_bay_id)?.name || ''
    : '';

  const otherSchedules = events.filter(
    (e) => e.maintenance_id === event.maintenance_id && e.id !== event.id
  );

  const tooltipContent = (
    <MaintenanceTooltipContent event={event} config={config} timeString={getTimeDisplayString()} employeeNames={employeeNames} serviceBayName={serviceBayName} otherSchedules={otherSchedules} tagName={colors.tagName} tagColor={colors.tagColor} />
  );

  return (
    <DraggableEvent
      event={event}
      eventType="maintenance"
      top={top}
      height={height}
      backgroundColor={colors.bg}
      borderColor={colors.border}
      ringColor={colors.ringColor}
      colorTagId={event.color_tag_id}
      colorTagMode={colors.tagMode}
      highlighted={isHighlighted}
      onClick={handleClick}
      dayIndex={dayIndex}
      gridStartHour={workStartHour}
      gridEndHour={workEndHour}
      overlapIndex={overlapIndex}
      overlapTotal={overlapTotal}
      tooltipContent={tooltipContent}
      resourceId={resourceId}
      sx={{
        animation: isHighlighted ? 'pulse 1s ease-in-out 3' : undefined,
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.02)' },
        },
      }}
    >
      {/* View Details button - show for events 1h or taller */}
      {durationHours >= 1 && (
        <Tooltip title={t('View maintenance details')}>
          <IconButton
            size="small"
            onClick={handleViewDetails}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              p: 0.5,
              color: colors.text,
              bgcolor: 'rgba(255, 255, 255, 0.6)',
              pointerEvents: 'auto',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Vehicle Registration and Work Number */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.25, pr: durationHours >= 1 ? 3.5 : 0 }}>
        <Typography
          sx={{
            ...plateFont,
            fontWeight: 700,
            fontSize: '12px',
            color: colors.text,
          }}
        >
          {event.vehicle_registration}
        </Typography>
        {event.work_number && (
          <Typography
            sx={{
              ...numFont,
              fontWeight: 500,
              fontSize: '10px',
              color: colors.text,
              opacity: 0.7,
            }}
          >
            #{event.work_number}
          </Typography>
        )}
      </Box>

      {/* Colour tag name. Translucent so it reads on any tag fill, on a light state
          background and on the search highlight alike. */}
      {colors.tagName && durationHours >= 1 && (
        <Box
          component="span"
          data-testid="calendar-event-color-tag-name"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            mb: 0.25,
            px: '6px',
            py: '1px',
            borderRadius: '10px',
            maxWidth: '100%',
            fontSize: '9px',
            fontWeight: 600,
            letterSpacing: '0.02em',
            color: colors.text,
            backgroundColor: colors.text === '#ffffff' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.45)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Box
            component="span"
            sx={{ width: 6, height: 6, borderRadius: '2px', backgroundColor: colors.tagColor, flexShrink: 0 }}
          />
          {colors.tagName}
        </Box>
      )}

      {/* Vehicle Model */}
      {durationHours >= 0.75 && (event.vehicle_manufacturer || event.vehicle_model) && (
        <Typography
          sx={{
            fontSize: '10px',
            color: colors.text,
            opacity: 0.75,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            mb: 0.25,
          }}
        >
          {[event.vehicle_manufacturer, event.vehicle_model].filter(Boolean).join(' ')}
        </Typography>
      )}

      {/* Description */}
      <Typography
        sx={{
          fontSize: '11px',
          color: colors.text,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {event.description}
      </Typography>

      {/* Customer name (show if there's space and customer is assigned) */}
      {durationHours >= 1 && event.customer_name && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
          <PersonIcon sx={{ fontSize: 12, color: colors.text, opacity: 0.8 }} />
          <Typography
            sx={{
              fontSize: '10px',
              color: colors.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2,
            }}
          >
            {event.customer_name}
          </Typography>
        </Box>
      )}

      {/* Parts order status badge */}
      {event.parts_order_status && PARTS_ORDER_CONFIG[event.parts_order_status] && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            mt: 0.25,
            padding: '1px 6px',
            borderRadius: '10px',
            fontSize: '9px',
            fontWeight: 500,
            backgroundColor: PARTS_ORDER_CONFIG[event.parts_order_status].bg,
            color: PARTS_ORDER_CONFIG[event.parts_order_status].color,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            whiteSpace: 'nowrap',
          }}
        >
          <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: PARTS_ORDER_CONFIG[event.parts_order_status].color }} />
          {t(`parts_order.${event.parts_order_status}_short`)}
        </Box>
      )}

      {/* Time (only show if event is tall enough) - show actual booking time range */}
      {durationHours >= 1.5 && (
        <Typography
          sx={{
            fontSize: '10px',
            color: colors.text,
            opacity: 0.85,
            mt: 0.5,
          }}
        >
          {getTimeDisplayString()}
        </Typography>
      )}

      {/* Service bay: last row so existing rows never move; hidden in bay view where the row already names it */}
      {durationHours >= 1.5 && currentView !== 'bay' && serviceBayName && (
        <Typography
          sx={{
            fontSize: '9px',
            color: colors.text,
            opacity: 0.85,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
            mt: 0.25,
          }}
        >
          📍 {serviceBayName}
        </Typography>
      )}
    </DraggableEvent>
  );
}

export default MaintenanceEvent;
